import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Activity } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { Reveal } from '@/components/Reveal';

const TILES = {
  osm: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attr: '&copy; OpenStreetMap contributors' },
  humanitarian: { url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', attr: '&copy; OpenStreetMap contributors, HOT' },
  satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attr: 'Tiles &copy; Esri' },
  topo: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}', attr: 'Tiles &copy; Esri' },
};

export const CITIES = [
  { id: 'erbil', pos: [36.19, 44.01], main: true },
  { id: 'mosul', pos: [36.34, 43.13], main: true },
  { id: 'baghdad', pos: [33.31, 44.37], main: true },
  { id: 'karbala', pos: [32.61, 44.02], main: true },
  { id: 'najaf', pos: [32.03, 44.33], main: true },
  { id: 'basra', pos: [30.51, 47.78], main: true },
  { id: 'duhok', pos: [36.87, 42.99] },
  { id: 'sulaymaniyah', pos: [35.56, 45.43] },
  { id: 'kirkuk', pos: [35.47, 44.39] },
];

const ROUTES = {
  north: [[36.87, 42.99], [36.34, 43.13], [36.19, 44.01]],
  erbilBaghdad: [[36.19, 44.01], [35.47, 44.39], [34.6, 44.3], [33.31, 44.37]],
  south: [[33.31, 44.37], [32.61, 44.02], [32.03, 44.33], [31.05, 46.26], [30.51, 47.78]],
  east: [[33.31, 44.37], [32.5, 45.8], [31.84, 47.14], [30.51, 47.78]],
  suly: [[35.56, 45.43], [35.47, 44.39]],
  west: [[33.31, 44.37], [33.42, 43.30]],
};

// Vehicle icons (inline SVG, brand colors)
const ICONS = {
  truck: '<path d="M1 3h15v13H1z M16 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
  car: '<path d="M5 17H3v-5l2-5h14l2 5v5h-2"/><path d="M5 12h14"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="16.5" cy="17.5" r="2.5"/>',
  van: '<path d="M2 7h12l4 4h4v6H2z"/><path d="M14 7v4h8"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="18" r="2.5"/>',
  bus: '<rect x="3" y="4" width="18" height="14" rx="2"/><path d="M3 10h18M8 4v6M16 4v6"/><circle cx="7" cy="19" r="2"/><circle cx="17" cy="19" r="2"/>',
};
const COLORS = { truck: '#0052FF', car: '#0EA5E9', van: '#14B8A6', bus: '#F59E0B' };

const VEHICLES = [
  { id: 'TR-204', type: 'truck', route: 'south', speed: 0.045, t: 0.1 },
  { id: 'KR-118', type: 'car', route: 'erbilBaghdad', speed: 0.06, t: 0.6 },
  { id: 'VN-072', type: 'van', route: 'east', speed: 0.05, t: 0.3 },
  { id: 'BS-310', type: 'bus', route: 'north', speed: 0.07, t: 0.5 },
  { id: 'TR-511', type: 'truck', route: 'west', speed: 0.08, t: 0.2 },
  { id: 'KR-902', type: 'car', route: 'suly', speed: 0.09, t: 0.8 },
];

function buildPath(points) {
  const segs = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const [a, b] = [points[i], points[i + 1]];
    const d = Math.hypot(b[0] - a[0], (b[1] - a[1]) * Math.cos((a[0] * Math.PI) / 180));
    segs.push({ a, b, d, start: total });
    total += d;
  }
  return { segs, total };
}

function pointAt(path, f) {
  const dist = f * path.total;
  const seg = path.segs.find((s) => dist <= s.start + s.d) || path.segs[path.segs.length - 1];
  const k = seg.d ? (dist - seg.start) / seg.d : 0;
  const lat = seg.a[0] + (seg.b[0] - seg.a[0]) * k;
  const lng = seg.a[1] + (seg.b[1] - seg.a[1]) * k;
  const heading = (Math.atan2((seg.b[1] - seg.a[1]) * Math.cos((lat * Math.PI) / 180), seg.b[0] - seg.a[0]) * 180) / Math.PI;
  return { lat, lng, heading };
}

function vehicleIcon(v, heading, speedKmh) {
  const flip = heading > 90 || heading < -90 ? -1 : 1;
  const html = `
    <div class="wiyak-vehicle" style="--c:${COLORS[v.type]}">
      <span class="wiyak-vehicle-ping"></span>
      <span class="wiyak-vehicle-body" style="transform:scaleX(${flip})">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${ICONS[v.type]}</svg>
      </span>
      <span class="wiyak-vehicle-label" dir="ltr">${v.id} · ${speedKmh} km/h</span>
    </div>`;
  return L.divIcon({ html, className: 'wiyak-vehicle-icon', iconSize: [34, 34], iconAnchor: [17, 17] });
}

function MovingVehicles() {
  const map = useMap();
  useEffect(() => {
    const paths = Object.fromEntries(Object.entries(ROUTES).map(([k, pts]) => [k, buildPath(pts)]));
    const state = VEHICLES.map((v) => ({ ...v, dir: 1, marker: null, speedKmh: 60 + Math.round(Math.random() * 40) }));
    state.forEach((s) => {
      const p = pointAt(paths[s.route], s.t);
      s.marker = L.marker([p.lat, p.lng], { icon: vehicleIcon(s, p.heading, s.speedKmh), interactive: false, zIndexOffset: 500 }).addTo(map);
      s.lastFlip = 1;
    });
    let raf;
    let last = performance.now();
    const loop = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      state.forEach((s) => {
        s.t += s.speed * s.dir * dt;
        if (s.t >= 1) { s.t = 1; s.dir = -1; }
        if (s.t <= 0) { s.t = 0; s.dir = 1; }
        const p = pointAt(paths[s.route], s.t);
        s.marker.setLatLng([p.lat, p.lng]);
        const heading = s.dir === 1 ? p.heading : p.heading + 180;
        const flip = heading > 90 || heading < -90 ? -1 : 1;
        if (flip !== s.lastFlip) { s.lastFlip = flip; s.marker.setIcon(vehicleIcon(s, heading, s.speedKmh)); }
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); state.forEach((s) => s.marker.remove()); };
  }, [map]);
  return null;
}

export default function IraqMap() {
  const { t, lang } = useLanguage();
  const style = TILES[t('coverage.mapStyle')] || TILES.osm;
  const mapRef = useRef(null);

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-brand-line bg-white p-6 shadow-[0_40px_80px_-40px_rgba(15,23,42,0.25)] sm:p-10" data-testid="iraq-map-card">
      <div className="pointer-events-none absolute inset-0 grid-bg opacity-60" />
      <div className="relative grid items-center gap-10 lg:grid-cols-12">
        <div className="relative lg:col-span-7" dir="ltr">
          <div className="overflow-hidden rounded-3xl border border-brand-line shadow-inner" data-testid="iraq-map-leaflet">
            <MapContainer ref={mapRef} center={[33.4, 44.2]} zoom={6} minZoom={5} maxZoom={9} scrollWheelZoom={false} style={{ height: 480, width: '100%', background: '#EAF2FB' }} attributionControl={false} zoomControl={false} key={t('coverage.mapStyle')}>
              <TileLayer url={style.url} attribution={style.attr} className={t('coverage.mapStyle') === 'osm' ? 'wiyak-tiles-soft' : ''} />
              {Object.entries(ROUTES).map(([k, pts]) => (
                <Polyline key={k} positions={pts} pathOptions={{ color: '#0052FF', weight: 3, opacity: 0.55, dashArray: '8 8' }} />
              ))}
              {CITIES.map((c) => (
                <CircleMarker key={c.id} center={c.pos} radius={c.main ? 7 : 5} pathOptions={{ color: '#fff', weight: 2, fillColor: c.main ? '#0052FF' : '#0EA5E9', fillOpacity: 1 }}>
                  <Tooltip permanent direction="top" offset={[0, -8]} className="wiyak-city-tip" key={`${c.id}-${lang}`}>{t(`coverage.cities.${c.id}`)}</Tooltip>
                </CircleMarker>
              ))}
              <MovingVehicles />
            </MapContainer>
          </div>

          <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-brand-ink/90 px-3.5 py-2 glass" data-testid="iraq-map-badge">
            <Activity className="h-3.5 w-3.5 text-brand-emerald" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-white">{t('coverage.nodes')} · {CITIES.length}</span>
          </div>
          <div className="absolute right-4 top-4 flex flex-col gap-1 rounded-2xl bg-white/90 p-2.5 shadow glass" data-testid="iraq-map-legend">
            {Object.entries(COLORS).map(([k, c]) => (
              <span key={k} className="inline-flex items-center gap-2 font-mono text-[9px] font-bold uppercase tracking-widest text-brand-steel"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} /> {t(`coverage.legend.${k}`)}</span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <Reveal>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-brand-ink sm:text-3xl">{t('coverage.title')}</h3>
          </Reveal>
          <Reveal delay={0.1}><p className="mt-4 leading-relaxed text-brand-steel">{t('coverage.sub')}</p></Reveal>
          <Reveal delay={0.18}>
            <div className="mt-6 grid grid-cols-2 gap-2.5" data-testid="iraq-city-grid">
              {CITIES.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-xl border border-brand-line bg-white px-3.5 py-2.5 transition-colors hover:border-brand-blue/40">
                  <span className="text-sm font-bold text-brand-ink">{t(`coverage.cities.${c.id}`)}</span>
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brand-dim">
                    <span className={`h-1.5 w-1.5 rounded-full ${c.main ? 'bg-brand-emerald glow-dot' : 'bg-brand-sky'}`} /> {t('coverage.online')}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-6 flex items-center justify-between rounded-2xl border border-brand-line bg-brand-alt px-5 py-4">
              <span className="font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-steel">{t('coverage.latency')}</span>
              <span className="font-mono text-xl font-bold text-brand-blue" dir="ltr">{t('coverage.latencyV')}</span>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
